<template>
	<div class="audio-player-wrapper" :style="wrapperStyle">
		<audio ref="audioPlayer" class="video-js vjs-default-skin"></audio>
	</div>
</template>

<script>
import videojs from "video.js";
import axios from "axios";
import 'video.js/dist/video-js.min.css';

export default {
	name: "AudioPlayer",
	props: {
		playerData: {
			type: Object,
			required: true,
		},
	},

	data() {
		return {
			player: null,
			last_saved_position: null,
			sent_watch: null,
			current_audio_num: 1,
			posterUrl: null,
		};
	},

	computed: {
		wrapperStyle() {
			if (this.posterUrl) {
				return {
					backgroundImage: `url(${this.posterUrl})`,
					backgroundSize: 'cover',
					backgroundPosition: 'center',
				};
			}
			return {};
		}
	},

	mounted() {
		this.initPlayer();
		window.audioPlayer = this.player;
	},

	beforeDestroy() {
		if (this.player) {
			this.player.dispose();
		}
		if (window.audioPlayer === this.player) {
			window.audioPlayer = null;
		}
	},

	methods: {
		initPlayer() {
			// Получаем текущее аудио
			const currentAudio = this.playerData.audios[this.current_audio_num - 1];
			if (!currentAudio || !currentAudio.sources || currentAudio.sources.length === 0) {
				console.error('No audio sources available');
				return;
			}

			// Устанавливаем poster URL если есть
			if (currentAudio.poster) {
				this.posterUrl = currentAudio.poster;
			}

			// Подготавливаем источники для VideoJS
			const sources = currentAudio.sources.map(s => ({
				src: s.src,
				type: s.type || "audio/mpeg"
			}));

			// Инициализируем VideoJS для аудио
			this.player = videojs(this.$refs.audioPlayer, {
				playbackRates: [0.5, 0.75, 1, 1.25, 1.5, 2],
				controls: true,
				preload: 'auto',
				fluid: true,
				responsive: true,
				sources: sources,
				fullscreen: {
					options: {
						navigationUI: 'hide'
					}
				}
			});

			// Отключаем полноэкранный режим для аудио
			this.player.ready(() => {
				const fullscreenButton = this.player.controlBar.getChild('fullscreenToggle');
				if (fullscreenButton) {
					fullscreenButton.hide();
				}
			});

			this.player.on('ready', this.onPlayerReady);
			this.player.on('timeupdate', this.onPlayerTimeUpdate);
			this.player.on('ended', this.onPlayerEnded);
			this.player.on('play', this.onPlayerPlay);
			this.player.on('error', this.onPlayerError);
		},

		onPlayerReady() {
			if (this.playerData.start_position > 0) {
				this.player.currentTime(this.playerData.start_position);
			}
			this.last_saved_position = this.playerData.start_position;
		},

		onPlayerTimeUpdate() {
			if (!this.player) return;

			const position = this.player.currentTime();
			const duration = this.player.duration();
			const end_pos = this.playerData.end_pos || (duration * 0.9);
			const rounded_position = Math.floor(position / 10) * 10;

			// Сохраняем позицию каждые 10 секунд
			if (
				rounded_position > 0 &&
				position < end_pos &&
				this.last_saved_position !== rounded_position
			) {
				this.last_saved_position = rounded_position;
				this.savePosition(rounded_position);
			}

			// Отмечаем как прослушанное при достижении 90%
			if (position > end_pos && !this.sent_watch) {
				this.markAsWatched();
			}
		},

		onPlayerEnded() {
			// Переходим к следующему аудио, если есть
			if (this.current_audio_num < this.playerData.audios.length) {
				this.current_audio_num++;
				this.loadAudio(this.current_audio_num - 1);
			} else {
				// Все аудио прослушаны
				this.markAsWatched();
			}
		},

		onPlayerPlay() {
			// Скрываем заглушку при начале воспроизведения
			const stub = document.querySelector('.j-hide-audio-stub');
			if (stub && !stub.classList.contains('not-showing')) {
				stub.classList.add('not-showing');
				setTimeout(() => {
					stub.classList.add("hide");
				}, 600);
			}
		},

		onPlayerError() {
			console.error('Audio player error:', this.player.error());
		},

		loadAudio(index) {
			const audio = this.playerData.audios[index];
			if (!audio || !audio.sources || audio.sources.length === 0) return;

			// Обновляем poster если есть
			if (audio.poster) {
				this.posterUrl = audio.poster;
			}

			const sources = audio.sources.map(s => ({
				src: s.src,
				type: s.type || "audio/mpeg"
			}));

			this.player.src(sources);
			this.player.load();
		},

		savePosition(position) {
			axios({
				response_type: "json",
				headers: {
					"X-Requested-With": "XMLHttpRequest",
					"X-CSRF-Token": document
						.querySelector('meta[name="csrf-token"]')
						.getAttribute("content"),
				},
				url: this.playerData.save_audio_position_path,
				method: "POST",
				data: {
					saved_audio_position: position,
				},
			})
				.then((response) => {
					this.playerData.start_position = position;
				})
				.catch((errors) => {
					console.warn("Failed to save audio position:", errors);
				});
		},

		markAsWatched() {
			if (this.sent_watch) return;

			axios({
				response_type: "json",
				headers: {
					"X-Requested-With": "XMLHttpRequest",
					"X-CSRF-Token": document
						.querySelector('meta[name="csrf-token"]')
						.getAttribute("content"),
				},
				url: this.playerData.listen_audio_path,
				method: "POST",
			})
				.then((response) => {
					this.sent_watch = true;
					// Обновляем страницу для отображения прогресса
					if (window.location) {
						window.location.reload();
					}
				})
				.catch((errors) => {
					console.warn("Failed to mark audio as watched:", errors);
				});
		},

		play() {
			if (this.player) {
				this.player.play();
			}
		},

		pause() {
			if (this.player) {
				this.player.pause();
			}
		},
	},
};
</script>

<style scoped>
.audio-player-wrapper {
	position: relative;
	border-radius: 5px;
	overflow: hidden;
	border: 1px solid var(--border-color);
	background-color: var(--light-bg);
	min-height: 200px;
}

.audio-player-wrapper .video-js {
	position: absolute;
	top: 50%;
	left: 0;
	width: 100%;
	height: 100px;
	transform: translateY(-50%);
	z-index: 2;
	background: transparent;
}

.audio-player-wrapper .video-js .vjs-big-play-button {
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	z-index: 3;
}

.audio-player-wrapper .video-js .vjs-fullscreen-control {
	display: none !important;
}
</style>
